import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronUp, HelpCircle, ArrowRight } from "lucide-react";
import ShareButton from "@/components/ShareButton";
import { LEARN_CONTENT } from "@/lib/learnContent";
import { useLearnLang, LangToggle } from "@/lib/learnLang";
import { useAuth } from "@/auth/AuthContext";

const QA = ({ q, a, isOpen, onToggle, index }) => (
  <li className="border-b border-[#E8ECEA]" data-testid="faq-item">
    <button
      type="button"
      className="w-full flex items-start justify-between gap-4 py-4 text-left"
      onClick={onToggle}
      data-testid={`faq-toggle-${index}`}
      aria-expanded={isOpen}
    >
      <span className="font-serif text-lg text-[#1C201F]">{q}</span>
      {isOpen
        ? <ChevronUp size={18} className="mt-1 flex-shrink-0 text-[#2F524A]" />
        : <ChevronDown size={18} className="mt-1 flex-shrink-0 text-[#5C6661]" />}
    </button>
    {isOpen && (
      <p className="pb-5 text-[#1C201F] leading-relaxed" data-testid={`faq-answer-${index}`}>{a}</p>
    )}
  </li>
);

const LearnFAQ = () => {
  const [openIdx, setOpenIdx] = useState(0);
  const [lang, setLang] = useLearnLang();
  const { user } = useAuth();
  const t = LEARN_CONTENT[lang].faq;

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-8 py-12 space-y-10" data-testid="learn-faq-page" lang={lang}>
      <header className="fade-up">
        <div className="flex items-center justify-between gap-4">
          <p className="eyebrow">{t.eyebrow}</p>
          <LangToggle lang={lang} onChange={setLang} />
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl mt-2 text-[#1C201F]" data-testid="faq-title">{t.title}</h1>
        <p className="text-lg text-[#5C6661] mt-4 leading-relaxed">
          {t.intro}{" "}
          <Link to="/learn" className="text-[#2F524A] font-semibold hover:underline">{t.intro_link}</Link>.
        </p>
        <div className="flex flex-wrap gap-3 mt-6">
          <ShareButton title={t.share_title} text={t.share_text} label={t.share_label} copiedLabel={t.copied} variant="primary" />
          <Link to="/learn" className="btn-secondary inline-flex items-center gap-2 text-sm" data-testid="faq-back-to-learn">
            <HelpCircle size={14} /> {t.back}
          </Link>
        </div>
      </header>

      <ul className="card-flat !p-2 fade-up delay-1" data-testid="faq-list">
        {t.items.map(([q, a], i) => (
          <QA key={i} q={q} a={a} index={i} isOpen={openIdx === i}
            onToggle={() => setOpenIdx(openIdx === i ? -1 : i)} />
        ))}
      </ul>

      <div className="card-flat fade-up delay-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 !p-8">
        <div>
          <p className="eyebrow">{t.still_eyebrow}</p>
          <h3 className="font-serif text-2xl mt-2">{t.still_title}</h3>
        </div>
        <Link to={user ? "/dashboard" : "/signup"} className="btn-primary inline-flex items-center gap-2" data-testid="faq-log-reading">
          {t.log_reading} <ArrowRight size={16} />
        </Link>
      </div>
    </div>
  );
};

export default LearnFAQ;
